import { useState, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Row, Col, InputGroup } from "react-bootstrap";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

import { isValidEmail } from "helpers/validationHelpers";
import { getCountries, getCurrencies, getProviderTeamsDetail, updateProviderTeams } from "api";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Input from "components/atoms/Input/Input";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import errorToast from "helpers/errorToast";
import Alert from "components/molecules/Alert/Alert";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import useAddressV2Fields from "hooks/useAddressV2Fields";
import { ICountries, IDropdown } from "pages/shipper/RequestForm/components/Schedule/Types";

interface TeamUser {
  email: string;
  selected: boolean;
}

interface ManageTeamsDialogProps {
  show: boolean;
  close: () => void;
  selectedTeam: string | null;
  setSelectedTeam: (team: string | null) => void;
  refetch: () => void;
}

function ManageTeamsAddressFieldsV2({ viewOnly }: { viewOnly: boolean }) {
  const { control, watch, setValue } = useFormContext();
  const { fields: addressV2Fields = [] } = useAddressV2Fields({
    viewOnly,
    control,
    watch,
    setValue,
    enable_new_address_structure: true,
    useV2: true,
  });

  const countryField = addressV2Fields.slice(0, 1)?.[0];
  const stateCityAndZipcodeField = addressV2Fields.slice(1);

  return (
    <>
      <Row className="mb-3">
        <Col sm={5} className={`font-weight-bold font-b1 ${countryField?.required ? "required" : ""}`}>
          {countryField?.label}
        </Col>
        <Col>
          <div className="w-192-px">{countryField?.children}</div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={5} className="font-weight-bold font-b1">
          <FormattedMessage id="general.address" defaultMessage="Address" />
        </Col>
        <Col>
          <Input
            type="text"
            name="address"
            placeholder="Address"
            className="rounded-input input-width-md"
            disabled={viewOnly}
          />
        </Col>
      </Row>

      {stateCityAndZipcodeField.map((field, index) => (
        <Row className="mb-3" key={`team-address-v2-${index}`}>
          <Col sm={5} className={`font-weight-bold font-b1 ${field.required ? "required" : ""}`}>
            {field.label}
          </Col>
          <Col>
            <div className="w-192-px">{field.children}</div>
          </Col>
        </Row>
      ))}
    </>
  );
}

function ManageTeamsAddressFieldsLegacy({ viewOnly, countries }: { viewOnly: boolean; countries: ICountries[] }) {
  return (
    <>
      <Row className="mb-3">
        <Col sm={5} className="font-weight-bold font-b1">
          <FormattedMessage id="general.address" defaultMessage="Address" />
        </Col>
        <Col>
          <Input
            type="text"
            name="address"
            placeholder="Address"
            className="rounded-input input-width-md"
            disabled={viewOnly}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={5} className="font-weight-bold font-b1">
          <FormattedMessage id="general.city" defaultMessage="City" />
        </Col>
        <Col>
          <Input
            type="text"
            name="city"
            placeholder="City"
            className="rounded-input input-width-md"
            disabled={viewOnly}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={5} className="font-weight-bold font-b1">
          <FormattedMessage id="general.country" defaultMessage="Country" />
        </Col>
        <Col>
          <div className="w-192-px">
            <Dropdown useHookForm name="country" options={countries} placeholder="Please select" disabled={viewOnly} />
          </div>
        </Col>
      </Row>
    </>
  );
}

function ManageTeamsDialog({ show, close, selectedTeam, setSelectedTeam, refetch }: ManageTeamsDialogProps) {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, setError, watch } = methods;
  const isInvoiceAddressV2FeatureEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_9731_INVOICE_ADDRESS_V2);
  const freightModes = watch("freight_modes") || [];
  const referenceNumber = watch("reference_number");
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const viewOnly = Boolean(!adminSettings?.edit_teams);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currencyOption, setCurrencyOption] = useState<IDropdown[]>([]);
  const [teams, setTeams] = useState<TeamUser[]>([]);
  const [teamSearch, setTeamSearch] = useState("");
  const [countries, setCountries] = useState<ICountries[]>([]);

  useEffect(() => {
    if (isInvoiceAddressV2FeatureEnabled) return;
    getCountries().then((res) => {
      setCountries(res.data);
    });
  }, [isInvoiceAddressV2FeatureEnabled]);

  useEffect(() => {
    getCurrencies()
      .then((res) => {
        if (res.data)
          setCurrencyOption(
            res.data.map((item: any) => ({
              ...item,
              label: item.name,
              value: item.code,
            })),
          );
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.packing-list.couldNotRetrieveCurrencies",
            defaultMessage: "Could not retrieve currencies. Please try again later.",
          }),
        ),
      );
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    if (!isInvoiceAddressV2FeatureEnabled && countries.length === 0) return;

    getProviderTeamsDetail(selectedTeam).then((res) => {
      res.data.request_notification_emails = res.data.request_notification_emails.join(", ");
      res.data.shipment_notification_emails = res.data.shipment_notification_emails.join(", ");
      res.data.billing_notification_emails = res.data.billing_notification_emails.join(", ");

      if (!isInvoiceAddressV2FeatureEnabled && countries.length > 0) {
        const country = countries.find((c: ICountries) => c.label === res.data.country);
        if (country) {
          res.data.country = country.value;
        }
      }

      const combinedTeams = res.data.unselected_team_users
        .map((user: string) => ({ email: user, selected: false }))
        .concat(res.data.team_users.map((user: string) => ({ email: user, selected: true })))
        .sort((a: TeamUser, b: TeamUser) => a.email.localeCompare(b.email));
      setTeams(combinedTeams);
      methods.reset(res.data);
    });
  }, [selectedTeam, isInvoiceAddressV2FeatureEnabled, countries]);

  const onSubmit = (data: any) => {
    if (viewOnly) return;
    setIsSubmitting(true);
    const {
      city,
      unselected_team_users,
      request_notification_emails,
      shipment_notification_emails,
      billing_notification_emails,
      ...restData
    } = data;

    const formatEmails = (emails: string, fieldName: string) => {
      if (!emails) return [];
      const emailArray = emails.split(",").map((email) => email.trim());
      const invalidEmails = emailArray.filter((email) => !isValidEmail(email));

      if (invalidEmails.length > 0) {
        setError(fieldName, {
          type: "invalid",
          message: "Invalid email format (comma-separated email)",
        });
        return;
      }
      return emailArray;
    };

    // Format data
    const formattedData = {
      ...restData,
      city: city || "",
      team_users: teams.filter((user) => user.selected).map((user) => user.email),
      request_notification_emails: formatEmails(request_notification_emails, "request_notification_emails"),
      shipment_notification_emails: formatEmails(shipment_notification_emails, "shipment_notification_emails"),
      billing_notification_emails: formatEmails(billing_notification_emails, "billing_notification_emails"),
    };

    updateProviderTeams(selectedTeam, formattedData)
      .then(() => {
        handleCloseDialog();
        refetch();
        toast("Team updated", { type: "success" });
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  const handleCloseDialog = () => {
    setSelectedTeam(null);
    methods.reset();
    close();
  };

  // Handle team checkbox changes
  const handleTeamChange = (index: number) => {
    const newCombinedTeams: TeamUser[] = [...teams];
    newCombinedTeams[index].selected = !newCombinedTeams[index].selected;
    setTeams(newCombinedTeams);
  };

  return (
    <Dialog
      show={show}
      title={
        viewOnly
          ? intl.formatMessage({
              id: "settings.user.viewTeam",
              defaultMessage: "View team",
            })
          : intl.formatMessage({
              id: "settings.user.editTeam",
              defaultMessage: "Edit team",
            })
      }
      onHide={handleCloseDialog}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((e) => onSubmit(e))}>
          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1 required">
              <FormattedMessage id="settings.provider.team.teamName" defaultMessage="Team name" />
            </Col>
            <Col>
              <Input
                type="text"
                required
                name="team_name"
                placeholder="Team name"
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.teamReference" defaultMessage="Team reference" />
            </Col>
            <Col>
              <div>{referenceNumber}</div>
            </Col>
          </Row>
          <hr className="mb-3 mt-3" />

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.contactPerson" defaultMessage="Contact person" />
            </Col>
            <Col>
              <Input
                type="text"
                name="contact_person"
                placeholder="Contact person"
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.contactPersonPhone" defaultMessage="Contact person phone" />
            </Col>
            <Col>
              <Input
                type="tel"
                name="contact_person_phone"
                placeholder="Contact person phone"
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.contactPersonEmail" defaultMessage="Contact person email" />
            </Col>
            <Col>
              <Input
                type="email"
                name="contact_person_email"
                placeholder="Contact person email"
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>

          {isInvoiceAddressV2FeatureEnabled ? (
            <ManageTeamsAddressFieldsV2 viewOnly={viewOnly} />
          ) : (
            <ManageTeamsAddressFieldsLegacy viewOnly={viewOnly} countries={countries} />
          )}

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1 required">
              <FormattedMessage id="general.currency" defaultMessage="Currency" />
            </Col>
            <Col>
              <div className="w-192-px">
                <Dropdown
                  useHookForm
                  name="currency"
                  options={currencyOption}
                  placeholder="Please select"
                  required
                  disabled={viewOnly}
                />
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.autoRemoveInvoices" defaultMessage="Auto remove invoices" />
            </Col>
            <Col>
              <Checkbox
                providerSections
                name="auto_remove_invoice"
                checkboxLabel={intl.formatMessage({
                  id: "settings.provider.team.autoRemoveInvoices",
                  defaultMessage: "Auto Remove Invoices",
                })}
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.addPDFtoEmail" defaultMessage="Add PDF to email" />
            </Col>
            <Col>
              <Checkbox
                providerSections
                name="add_pdf_to_emails"
                checkboxLabel={intl.formatMessage({
                  id: "settings.provider.team.addPDFtoEmail",
                  defaultMessage: "Add PDF to Email",
                })}
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.notifications.requestNotifications" defaultMessage="Request notification" />
            </Col>
            <Col>
              <Input
                type="text"
                name="request_notification_emails"
                placeholder={intl.formatMessage({
                  id: "general.commaSeparatedEmails",
                  defaultMessage: "Comma-separated e-mails",
                })}
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.notifications.shipmentNotifications" defaultMessage="Shipment notification" />
            </Col>
            <Col>
              <Input
                type="text"
                name="shipment_notification_emails"
                placeholder={intl.formatMessage({
                  id: "general.commaSeparatedEmails",
                  defaultMessage: "Comma-separated e-mails",
                })}
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.notifications.billingNotifications" defaultMessage="Billing notification" />
            </Col>
            <Col>
              <Input
                type="text"
                name="billing_notification_emails"
                placeholder={intl.formatMessage({
                  id: "general.commaSeparatedEmails",
                  defaultMessage: "Comma-separated e-mails",
                })}
                className="rounded-input input-width-md"
                disabled={viewOnly}
              />
            </Col>
          </Row>
          <hr className="mb-3 mt-3" />
          <Row className="mb-3">
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="general.freightModes" defaultMessage="Freight Modes" />
            </Col>
            <Col>
              {freightModes.map((mode: string) => (
                <div>{mode}</div>
              ))}
              <Alert className="mb-0 mt-3" variant="warning">
                <FormattedMessage
                  id="settings.provider.team.freightModeSettingsCannotBeUpdated"
                  defaultMessage="Freight mode settings can only be updated by Cargobase"
                />
              </Alert>
            </Col>
          </Row>
          <Row>
            <Col sm={5} className="font-weight-bold font-b1">
              <FormattedMessage id="settings.provider.team.teamUsers" defaultMessage="Team users" />
            </Col>
            <Col>
              <ExpandableSearch
                containerClasses="mb-3"
                value={teamSearch}
                alwaysExpanded
                onChange={(e) => setTeamSearch(e.target.value)}
              />
              <div style={{ maxHeight: "203px", overflowY: "scroll" }}>
                {teams.map((user: TeamUser, index) => {
                  if (teamSearch && !user.email.toLowerCase().includes(teamSearch.toLowerCase())) return;
                  return (
                    <InputGroup key={index} className="checkbox bg-transparent mb-3">
                      <label htmlFor={user.email} className="d-flex align-teams-center cursor-pointer mb-0 w-100">
                        <input
                          id={user.email}
                          type="checkbox"
                          disabled={viewOnly}
                          className="d-none"
                          onChange={() => handleTeamChange(index)}
                          checked={user.selected}
                        />
                        <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                        <span className="text-primary mr-auto">{user.email}</span>
                      </label>
                    </InputGroup>
                  );
                })}
              </div>
            </Col>
          </Row>
          <Col className="mt-4">
            <Row className="d-flex justify-content-between">
              <Button
                className="rounded-pill mt-3 font-b1 btn-size"
                variant="outline-primary"
                label={intl.formatMessage({ id: "general.discard", defaultMessage: "Discard" })}
                onClick={close}
              />
              {!viewOnly && (
                <Button
                  className="rounded-pill mt-3 font-b1 btn-size"
                  variant="primary"
                  label={intl.formatMessage({ id: "settings.user.editTeam", defaultMessage: "Edit team" })}
                  type="submit"
                  isLoading={isSubmitting}
                />
              )}
            </Row>
          </Col>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default ManageTeamsDialog;
